import { useEffect, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { toast } from 'sonner'
import { Modal } from './ui/Modal'
import { Button } from './ui/Button'
import { TextField, FieldLabel } from './ui/Field'
import { api, extractApiError } from '../lib/api'
import { folderListResponse } from '../lib/types'
import { CONTAINS_LETTER } from '../lib/validators'

const ROOT_FOLDER_VALUE = '__root__'

const listSchema = z.object({
  name: z
    .string({ message: 'List Name is required.' })
    .trim()
    .min(1, 'List Name is required.')
    .min(2, 'List Name must be at least 2 characters.')
    .max(100, 'List Name must be at most 100 characters.')
    // Numbers-only / symbols-only names aren't meaningful — require a letter.
    .regex(CONTAINS_LETTER, 'List Name must contain at least one letter.'),
})

export function ListCreateDialog({
  open,
  defaultSpaceId,
  // Optional Project (folder) to pre-select — used when creating a List
  // straight from a Project's row menu so it lands inside that Project.
  defaultFolderId,
  onClose,
  onCreated,
}) {
  const qc = useQueryClient()
  const spaceId = defaultSpaceId
  const [folderId, setFolderId] = useState(null)

  const form = useForm({
    resolver: zodResolver(listSchema),
    defaultValues: { name: '' },
  })

  const foldersQuery = useQuery({
    queryKey: ['folders', spaceId],
    enabled: open && Boolean(spaceId),
    queryFn: async () => {
      if (!spaceId) return { data: [] }
      const res = await api.get(`/spaces/${spaceId}/folders`)
      return folderListResponse.parse(res.data)
    },
  })
  const folders = foldersQuery.data?.data ?? []

  useEffect(() => {
    if (open) {
      setFolderId(defaultFolderId ?? null)
      form.reset({ name: '' })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, defaultSpaceId, defaultFolderId])

  const createMutation = useMutation({
    mutationFn: async (values) => {
      if (!spaceId) throw new Error('No Space context.')
      const res = await api.post(`/spaces/${spaceId}/lists`, {
        name: values.name.trim(),
        folderId,
      })
      return res.data
    },
    onSuccess: (created) => {
      toast.success('List has been created successfully.')
      void qc.invalidateQueries({ queryKey: ['lists', spaceId] })
      onClose()
      if (spaceId) onCreated?.(created.id, spaceId)
    },
    onError: (err) => {
      const { message, fields } = extractApiError(err)
      if (fields?.name) form.setError('name', { message: fields.name })
      else toast.error(message)
    },
  })

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Create List"
      description="All Lists are located within a Space. Lists can house any type of task."
      size="md"
      footer={
        <Button
          type="submit"
          form="list-create-form"
          loading={createMutation.isPending}
          disabled={!spaceId}
          className="ml-auto"
        >
          Create
        </Button>
      }
    >
      <form
        id="list-create-form"
        onSubmit={form.handleSubmit((v) => createMutation.mutate(v))}
        noValidate
        className="space-y-4"
      >
        <TextField
          label="List Name"
          required
          placeholder="Enter a List Name"
          minLength={2}
          maxLength={80}
          error={form.formState.errors.name?.message}
          {...form.register('name')}
        />

        {/* Project picker — hidden when a Project is pre-selected (e.g.
         *  creating from a Project's row menu), since the destination is
         *  already fixed. Shown when creating from the Space, where
         *  choosing a Project (or none) is a real decision. */}
        {!defaultFolderId ? (
          <div>
            <FieldLabel label="Project (Optional)" htmlFor="list-folder" />
            <select
              id="list-folder"
              value={folderId ?? ROOT_FOLDER_VALUE}
              onChange={(e) =>
                setFolderId(e.target.value === ROOT_FOLDER_VALUE ? null : e.target.value)
              }
              disabled={!spaceId || foldersQuery.isLoading}
              className="w-full h-10 px-3 rounded-md border border-[color:var(--color-rule)] bg-[color:var(--color-card)] text-[color:var(--color-ink)] outline-none focus:border-[color:var(--color-accent)] cursor-pointer disabled:opacity-60"
            >
              <option value={ROOT_FOLDER_VALUE}>(No project — at Space root)</option>
              {folders.map((f) => (
                <option key={f.id} value={f.id}>
                  {f.name}
                </option>
              ))}
            </select>
          </div>
        ) : null}
      </form>
    </Modal>
  )
}
